import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

import { removeOrArchiveSingleTargeting } from './Targetings.utils';

export const MAX_LOCATIONS = 50;

interface IProps {
  campaign?: Campaign;
  targetings: IObservableArray<Targeting>;
}

interface IUseLocationsReturn {
  filteredTargetings: Targeting[];
  remainingLocations: number;
  canAddMore: boolean;
  hasInvalidTargetings: boolean;
  addFilterErrors: string[];
  handleAddFilter: () => void;
  handleRemoveFilter: (targeting: Targeting) => void;
}

export function useLocationFilters({
  campaign,
  targetings,
}: Readonly<IProps>): IUseLocationsReturn {
  const { Targetings } = useStore();
  const { t } = useTranslation();

  // Direct computation - MobX will handle reactivity
  const filteredTargetings = targetings.filter(
    (t) => t.get('is_archived') !== true && t.get('kind') === 'geo',
  );

  const remainingLocations = MAX_LOCATIONS - filteredTargetings.length;
  const hasInvalidTargetings = filteredTargetings.some((targeting) => !targeting.isValid());
  const canAddMore = remainingLocations > 0 && !hasInvalidTargetings;

  const addFilterErrors = useMemo(() => {
    const errors: string[] = [];
    if (remainingLocations <= 0) {
      errors.push(t('Maximum of {{count}} locations reached', { count: MAX_LOCATIONS }));
    }
    if (hasInvalidTargetings) {
      errors.push(t('Complete all filters before adding more'));
    }
    return errors;
  }, [remainingLocations, hasInvalidTargetings, t]);

  const handleRemoveFilter = useCallback(
    (targeting: Targeting): void => {
      removeOrArchiveSingleTargeting(targeting, targetings, Targetings);
    },
    [targetings, Targetings],
  );

  const handleAddFilter = useCallback((): void => {
    const currentFiltered = targetings.filter(
      (t) => t.get('is_archived') !== true && t.get('kind') === 'geo',
    );

    if (currentFiltered.length >= MAX_LOCATIONS) {
      return;
    }

    const model = Targetings.create({
      parent: campaign?.get('id'),
      kind: 'geo',
      included: true,
    });
    runInAction(() => {
      targetings.push(model);
    });
  }, [campaign, targetings, Targetings]);

  return {
    filteredTargetings,
    remainingLocations,
    canAddMore,
    hasInvalidTargetings,
    addFilterErrors,
    handleAddFilter,
    handleRemoveFilter,
  };
}

export default useLocationFilters;
